import {
	Avatar,
	Grid,
	Paper,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { api } from "api";
import { UserBasic, UserListBasic } from "api/types";
import crown from "assets/icons/crown.svg";
import { InfoSnackBar } from "components/InfoSnackBar";
import { getInitials } from "helpers/authValidation";
import { LEADER_BOARD } from "messages";
import { useEffect, useRef, useState } from "react";
import { UserRank } from "./types";

const PREFIX = "StudentLeaderboardPageComponent";

const classes = {
	avatar: `${PREFIX}-avatar`,
};

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.avatar}`]: {
		color: theme.palette.primary.contrastText,
		backgroundColor: theme.palette.secondary.main,
		padding: theme.spacing(3),
	},
}));

export interface StudentLeaderboardPageComponentProps {
	assignmentId: string;
	myId?: string;
}

export const StudentLeaderboardPageComponent = ({
	assignmentId,
	myId,
}: StudentLeaderboardPageComponentProps): JSX.Element => {
	const [leaderBoard, setLeaderBoard] = useState<UserRank[]>([]);
	const [myRank, setMyRank] = useState<UserRank>();
	const componentMounted = useRef(true);
	const [err, setErr] = useState(false);

	useEffect(() => {
		api
			.getAssignmentLeaderboard(assignmentId)
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					throw new Error();
				} else {
					if (componentMounted) {
						const LeaderBoardData: UserListBasic = res.data;
						const userListRank: UserRank[] = [];
						let userRank: UserRank;
						for (let i = 0; i < LeaderBoardData.length; i++) {
							const leaderBoardUser: UserBasic = LeaderBoardData[
								i
							] as UserBasic;
							userRank = {
								rank: i + 1,
								...leaderBoardUser,
							};
							userListRank.push(userRank);
							if (leaderBoardUser.userId === myId) {
								setMyRank(userRank);
							}
						}
						setLeaderBoard(userListRank);
					}
				}
			})
			.catch(() => {
				setErr(true);
			});
		return () => {
			componentMounted.current = false;
		};
	}, [assignmentId, myId]);

	return (
		<Root>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<TableContainer component={Paper}>
						<Table sx={{ minWidth: 650 }} aria-label="simple table">
							<TableHead>
								<TableRow>
									<TableCell>Rank</TableCell>
									<TableCell align="left">Portrait</TableCell>
									<TableCell align="left">Name</TableCell>
									<TableCell align="left">Score</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{leaderBoard.map((item) => (
									<TableRow
										key={item.userId}
										sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
									>
										<TableCell component="th" scope="row">
											{item.rank}
										</TableCell>
										<TableCell align="right" scope="row">
											<Avatar
												alt={`Avatar n°${item.userId}`}
												className={classes.avatar}
											>
												{item && getInitials(item.firstName, item.lastName)}
											</Avatar>
										</TableCell>
										<TableCell align="left">
											<Typography>
												{item.firstName} {item.lastName}
											</Typography>
										</TableCell>
										<TableCell align="left">
											<Typography>0</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h4" data-testid="leaderboard">
						My Rank
						<img
							data-testid="crown"
							src={crown}
							alt="crown"
							style={{ maxHeight: 60, maxWidth: 60 }}
						/>
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<TableContainer component={Paper}>
						<Table sx={{ minWidth: 650 }} aria-label="simple table">
							<TableHead>
								<TableRow>
									<TableCell>My Rank</TableCell>
									<TableCell align="left">Portrait</TableCell>
									<TableCell align="left">Name</TableCell>
									<TableCell align="left">Score</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow
									key={myRank?.userId}
									sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
								>
									<TableCell component="th" scope="row">
										{myRank?.rank}
									</TableCell>
									<TableCell align="right" scope="row">
										<Avatar className={classes.avatar}>
											{myRank &&
												getInitials(myRank?.firstName, myRank?.lastName)}
										</Avatar>
									</TableCell>
									<TableCell align="left">
										<Typography>
											{myRank?.firstName} {myRank?.lastName}
										</Typography>
									</TableCell>
									<TableCell align="left">
										<Typography>0</Typography>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</Grid>
			</Grid>
			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={LEADER_BOARD}
				severity="error"
			/>
		</Root>
	);
};
